import React, { useEffect, useState } from "react";
import { Grid, Typography, Modal, Box, Rating, Button, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import OrderSummary from "../../components/createSecondaryOrder/OrderSummary";
import OrderDetailsPrimary from "../../components/createSecondaryOrder/OrderDetailsPrimary";
import success from "../../assets/gif/success.gif";
import FormHeader from "../dms_common/FormHeader";
import { ReactComponent as PercentIcon } from "../../assets/image/svg/percentageIconBlue.svg";
import { ChannelkartNetworkPost, OrderRatingPost } from "../../services/networkService";
import CartSummaryFooter from "../dms_common/CartSummaryFooter";
import { getActivityReminderPayload } from "./util";
import { getDataFromLocalDb, insertDataIntoLocalDB } from "../../services/dbService";
import { prepareOrderReqBody } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { OrderType } from "../../types/types";
import {
  customStyle,
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../../features/viewOrder/ViewOrderUtils";
import CustomGenericModal from "../dms_common/CustomGenericModal";
import "./CheckoutSummary.css";
import ScreenOverlay from "../dms_common/ScreenOverlay";
import { ReactComponent as DiscountIcon } from "../../assets/image/svg/discount.svg";
import { logEvent } from "firebase/analytics";
import { analytics, performance } from "../../../src/firebase";
import useShortcutKeys from "../dms_common/ShortcutKeys";

const CheckoutSummaryPrimary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const outletId = query.get("outletId") || "null";
  const orderType = (query.get("orderType") as OrderType) || OrderType.SECONDARY;
  const locationState: any = location.state as any;
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [submitOrderLoading, setSubmitOrderLoading] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);
  const [ratingLoading, setRatingLoading] = useState<boolean>(false)

  const orderValidateRes = locationState?.orderValidateRes?.[0] || {};

  const handleSubmitOrder = async () => {
    setSubmitOrderLoading(true);
    try {
      const orderReqBody = prepareOrderReqBody(JSON.parse(JSON.stringify(orderValidateRes)), orderType, locationState);
      const response = await ChannelkartNetworkPost("/orders", orderReqBody);
      if (response?.status !== 200 && response?.status !== 201) {
        logEvent(analytics, "primary_order_failed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "error_reason": response
        });
        setGenericModalProps(
          getGenericModalProps("error", "There is some issue while placing order")
        );
      } else {
        
        logEvent(analytics, "primary_order_submitted", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "order_id": response?.data?.feature?.[0]?.id ?? null,
          "order_value": response?.data?.feature?.[0]?.totalAmount ?? null,
          "sku_count": response?.data?.feature?.[0]?.lineCount ?? null,
          "payment_mode": locationState?.paymentMethod ?? "",
          "total_discount": response?.data?.feature?.[0]?.discountInfo?.[0]?.discount ?? null,
          "company_id": response?.data?.feature?.[0]?.supplier ?? null,
        });

        setOpen(true);
      }
    } catch (error) {
      console.error(error);
      logEvent(analytics, "primary_order_failed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "error_reason": error
      });
      setGenericModalProps(
        getGenericModalProps("error", "There is some issue while placing order")
      );
    } finally {
      setSubmitOrderLoading(false);
    }
  };

  const handleStepperBack = () => {
    navigate(location.pathname.replace(/\/([^/]*)$/, `?outletId=${outletId}`));
  };

  const handleNavigateToCatalogue = () => {
    if(orderType === OrderType.SECONDARY) {
    navigate(`/dashboard/sale/createSecondaryOrder?outletId=${outletId}`);
    }else if(orderType === OrderType.PRIMARY){
    navigate(`/dashboard/purchase/createPrimaryOrder`);
    }
  }

  const clearCartDataFromLocalDB = async () => {
    const res = await getDataFromLocalDb("Cart");
    const localDbCartData = res?.data?.[0]?.data || {};
    const updatedCartData = {
      ...localDbCartData,
    };
    delete updatedCartData[outletId];
    await insertDataIntoLocalDB("Cart", [
      {
        id: 1,
        data: updatedCartData,
      },
    ]);
  };

  const handleCompletion = async () => {
    setRatingLoading(true)
    try {

      setOpen(false);
      clearCartDataFromLocalDB();
      orderType === OrderType.PRIMARY
        ? navigate(`/dashboard/purchase/viewPrimaryOrders`)
        : navigate(`/dashboard/sale/manageOrders`);
    } catch (e) {
      console.log(e);
    } finally {
      setRatingLoading(false)
    }
  }

  const totalAmountAndQtyInfo = {
    numberOfProducts: orderValidateRes?.lineCount,
    totalCaseQuantity: orderValidateRes?.totalCaseQuantity,
    totalPieceQuantity: orderValidateRes?.totalPieceQuantity,
    totalOtherUnitQuantity: orderValidateRes?.totalOtherUnitQuantity,
    totalAmount: orderValidateRes?.totalAmount,
    netAmount: orderValidateRes?.netAmount
  };

useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      event.preventDefault();

        if (open && !ratingLoading) {
          handleCompletion();
        } else if (!submitOrderLoading && !open) {
          handleSubmitOrder();
        }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [submitOrderLoading, open, ratingLoading, handleSubmitOrder, handleCompletion]);
const { positiveCta, negativeCta } = useShortcutKeys();
  return (
    <div className="CheckoutSummary_container">
      <ScreenOverlay apply={submitOrderLoading || ratingLoading} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        modalOpen={genericModalProps?.open}
        onClose={() => {
          setGenericModalProps(initialGenericModalProps);
          handleStepperBack();
        }}
      />

      <Modal open={open} onClose={() => {}} className="successful_message_pop">
        <Box className="successful_message_box">
          <img src={success} alt="Success" className="successful_message_img" />
          <Typography className="successful_message_heading">Successful!</Typography>
          <Typography className="successful_message_subtitle">
            Your order is successfully placed.
          </Typography>
          {/* <Box className="successful_message_rating">
            <Rating
              name="simple-controlled"
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              precision={1}
              className="custom_rating"
            />
            <Typography className="successful_rating_message">How was everything?</Typography>
          </Box> */}
          <Button
            variant="contained"
            onClick={handleCompletion}
            className="successful_message_button"
            sx={{
              minWidth: "130px",
              minHeight: "37px",
              boxShadow: "none",
            }}
          >
            {ratingLoading ? <CircularProgress style={customStyle} /> : "Continue"}
            {!ratingLoading && <span style={{fontSize:"9.5px"}}>&nbsp;{positiveCta}</span>}
          </Button>
        </Box>
      </Modal>

      <FormHeader
        parentLabel={"Place New Order"}
        label={"Check out"}
        goBackToParent={handleStepperBack}
        childLabel="Order Summary"
        childLableCaseClick={handleNavigateToCatalogue}
      />

      <Grid container spacing={2} className="grid_container" sx={{ height: "86%", marginTop: "0" }}>
        <Grid item xs={5} sx={{ height: "100%" }}>
          <div style={{height: "100%", overflow: "auto", scrollbarWidth: "none", msOverflowStyle: "none"}}>
          <OrderSummary
            outletCode={orderValidateRes?.outletCode || 0}
            outletName={orderValidateRes?.outletName || 0}
            billDate={locationState?.billDate}
            billToAddress={locationState?.billToAddress}
            deliveryDate={locationState?.deliveryDate}
            shipToAddress={locationState?.shipToAddress}
            salesRep={locationState?.salesRep || ""}
            salesType={locationState?.salesType || ""}
            customerChannel={locationState?.customerChannel || ""}
            paymentMethod={locationState?.paymentMethod || ""}
            totalAmount={orderValidateRes?.totalAmount || 0}
            finalAmount={orderValidateRes?.netAmount || 0}
            couponDiscount={orderValidateRes?.couponValue || 0}
            schemeDiscount={orderValidateRes?.discountValue || 0}
            taxAmount={orderValidateRes?.totalTaxAmount || 0}
          />
          {orderValidateRes?.discountInfo?.length > 1 && <div className="order_summary_container" style={{ marginTop: "0.7rem", padding: 0 }}>
            <div className="sub_header">
              <div
                className="confirmed_order_icon"
                style={{ display: "flex", alignItems: "center" }}
              >
                <PercentIcon width={22} height={22} />
              </div>
              <Typography sx={{ fontWeight: 500, fontSize: "16px !important", color: "#227C9D" }}>
                Discounts Applicable
              </Typography>
            </div>
            <div className="checkout_summary_discounts_box">
              <div style={{ color: "#00C6B1", display: "flex", alignItems: "center" }}>
                <DiscountIcon width={18} height={18} />
                <span style={{fontSize: "14px", fontWeight: 500, marginLeft: "6px"}}>Coupons</span>
              </div>
              <div>
                {orderValidateRes?.discountInfo?.map((discount: any) => {
                  return (
                    !["summary"].includes(discount?.discountType?.toLowerCase()) && (
                      <p key={discount?.id} style={{fontSize: "13px", fontWeight: 450, lineHeight: "1.3", paddingTop: "8px"}}>{discount?.discountInfo ?? ""}</p>
                    )
                  );
                })}
              </div>
            </div>
          </div>}
          </div>
        </Grid>
        <Grid item xs={7} sx={{ height: "100%" }}>
          <OrderDetailsPrimary
            orderDetails={orderValidateRes?.orderDetails || []}
            skuCodeSortOrder={locationState?.skuCodeSortOrder}
          />
        </Grid>
      </Grid>
      <div className="Checkout_cartSummaryFooter_container_wrapper">
        <div className="Checkout_cartSummaryFooter_container">
          <CartSummaryFooter
            isPrimary={true}
            cartProducts={null}
            onButtonClick={handleSubmitOrder}
            buttonText={"Submit Order"}
            totalAmountAndQtyInfo={totalAmountAndQtyInfo}
            loading={submitOrderLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckoutSummaryPrimary;
